import React from "react";
import { Button, Spacer, VDBText } from "@components/atoms";
import { ButtonCategory } from "@components/atoms/Button/types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { SpaceType } from "@components/atoms/Spacer/types";
import { MessageContainerProps } from "@components/molecules/MessageContainer/types";
import RenderHtml from "react-native-render-html";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTheme } from "styled-components/native";
import styled, { css } from "styled-components/native";
import { Platform } from "react-native";
import useInternetStatus from "@components/common/hooks/useInternetStatus";
import { useTranslation } from "react-i18next";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";

const MessageContainer = ({
  messageTitle,
  buttons,
  iconName,
  iconColor,
  message,
  internetPreference = false,
}: MessageContainerProps) => {
  const { deviceWidth: contentWidth } = useIsFoldable();
  const typography = useTypographyStore();
  const theme = useTheme();
  const internetStatus = useInternetStatus();
  const { t } = useTranslation();
  const htmlSource = {
    html:
      internetPreference && !internetStatus
        ? t(
            "global.text.internetDisconnected",
            "Oops, something went wrong. Please try again later."
          )
        : message
        ? `<p style="font-family: Roboto; font-size: 14px; font-style: normal; font-weight: 400; line-height: 20px; color: ${theme["core-06"]}; margin: 0; text-align:center;">
            ${message}
          </p>`
        : t(
            "global.text.somethingWentWrongDescription",
            '<p>Oops, something went wrong. Please try again later. If this doesn\'t get resolved soon, contact <a href="mailto:support@vdbapp.com">support@vdbapp.com</a></p>'
          ),
  };

  const tagsStyles = {
    body: { ...typography["Body-R"], color: theme["core-06"] },
  };

  return (
    <Overlay>
      <CenterContainer>
        {iconName && (
          <>
            <VDBIcon
              name={iconName}
              variant={IconsVariant.VDBCustom}
              size={64}
              color={iconColor}
            />
            <Spacer size={SpaceType.large} />
          </>
        )}
        {messageTitle && (
          <>
            <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
              {messageTitle}
            </VDBText>
            <Spacer size={SpaceType.Basic} />
          </>
        )}
        {Platform.OS === "web" ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
            dangerouslySetInnerHTML={{ __html: htmlSource?.html }}
          />
        ) : (
          <>
            {htmlSource && (
              <RenderHtml
                contentWidth={contentWidth}
                source={htmlSource}
                tagsStyles={tagsStyles}
              />
            )}
          </>
        )}
        {Platform.OS === "web" && <div />}
        <Spacer size={SpaceType.ExtraLarge} />
        <ButtonOverLay>
          <Button
            type={buttons.type}
            category={ButtonCategory.default}
            title={buttons.title}
            onPress={buttons.onPressAction}
            loading={buttons.loading}
            disabled={buttons.disabled}
            width={buttons.width}
            textTypographyStyle={buttons.textTypographyStyle}
          />
        </ButtonOverLay>
      </CenterContainer>
    </Overlay>
  );
};

const ButtonOverLay = styled.View`
  width: 100%;
`;

const Overlay = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  padding-horizontal: 16px;
`;

const CenterContainer = styled.View`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  ${({ theme }) =>
    (theme.isLargeScreen || theme.isTablet) &&
    css`
      max-width: 343px;
    `}
`;

export default MessageContainer;
